import { useQuery } from '@tanstack/react-query';
import { getMe, getStorefront } from '../api';

/**
 * The activation-kit grid for a member with NO active slot — the one body
 * shared by the dashboard's "Pick your activation kit" card and the
 * Browse-kits `KitPickerDialog`. The two used to be near-identical copies
 * whose Buy Now behaviour drifted (`add` vs `addOnly`); this is the merge.
 *
 * No slotId ON PURPOSE — do not "fix" this to pass the active slot. This
 * only ever renders for a member with no active slot, so there is nothing to
 * price against and the server falls back to base price. The bare
 * `['storefront']` key is likewise deliberate: it must NOT share a cache
 * entry with the slot-keyed `['storefront', slotId]` used everywhere else.
 *
 * The server already applies the campaign kit scope for a slotless caller and
 * `createOrderInTx` is the authoritative gate (`product_not_in_package`); the
 * `registeredLinkKitIds` filter here is belt-and-braces on top of that, and
 * also covers a campaign whose tags changed after the catalog was cached.
 */
export function useActivationKits(enabled = true) {
  const storefrontQ = useQuery({
    queryKey: ['storefront'],
    queryFn: () => getStorefront(),
    enabled,
  });
  const meQ = useQuery({ queryKey: ['me'], queryFn: getMe, enabled });

  const allKits = (storefrontQ.data?.products ?? []).filter((p) => p.isKit);
  // Replicated-link packages: scope the lead to the activation kits tagged to
  // the campaign page they signed up through. null = no restriction (show all).
  const linkKitIds = meQ.data?.registeredLinkKitIds ?? null;
  const kits = linkKitIds ? allKits.filter((p) => linkKitIds.includes(p.id)) : allKits;

  return { kits, allKits, isLoading: storefrontQ.isLoading || meQ.isLoading };
}
